import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Tooltip, Chip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";

import "./B4EmissionFactors.scss"

import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { preventScientificNotation } from "../../../../../services/utilities";
import { frameworks, gases } from "../../Constants";

const B4EmissionFactors = forwardRef(({ b4EmissionFactors, fuelOriginMsts, vesselMstId, refetchMonitoringData, fuelBunkeredDensities }, ref) => {
    const [activeAction, setactiveAction] = useState(-1);
    const [emissionFactorData, setEmissionFactorData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [fuelData, setFuelData] = useState([]);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deletingRowIndex, setDeletingRowIndex] = useState();

    const RowActions = {
        Edit: 0,
        Add: 1,
    };

    useEffect(() => {
        const transformed = transformData(b4EmissionFactors);
        setFuelData(transformed);
    }, [b4EmissionFactors]);

    const transformData = (data) => {
        const getFrameworkData = (arr = [], framework) =>
            arr?.find((x) => x.legalFrameWork === framework) || {};
        const mapGases = (item, gas, slipEditable) =>
            frameworks.reduce((acc, fw) => {
                const data = getFrameworkData(item[gas], fw);
                acc[fw] = {
                    factorReference: data.factorReference || 'Default',
                    ttwEf: data.ttwEf || 0,
                    slipEf: data.slipEf || 0,
                    isTtwEditable: data.isTtwEditable,
                    isSlipEditable: slipEditable,
                    legalFrameWork: fw,
                    methodologiesForEFs: data?.methodologiesForEFs
                };
                return acc;
            }, {});
        return data?.map((item) => {
            return {
                b4EmissionFactorId: item.b4EmissionFactorId ?? 0,
                fuelOrigin: item.fuelOrigin,
                fuelType: item.fuelType,
                nameOfOtherFuel: item.nameOfOtherFuel,
                bunkeredFuelDensity: item.bunkeredFuelDensity,
                fuelDescription: item.fuelDescription,
                co2: mapGases(item, 'co2', false),
                ch4: mapGases(item, 'ch4', false),
                n2o: mapGases(item, 'n2o', false),
            };
        });
    };

    const handleFuelActions = async (table, row, action) => {
        if (activeAction !== -1) return;
        const isEdit = action === RowActions.Edit;
        const isAdd = action === RowActions.Add;
        setactiveAction(action);
        setEmissionFactorData(isEdit ? row?.original : []);
        isEdit
            ? table.setEditingRow(row)
            : isAdd && table.setCreatingRow(true);
    };

    const resetActiveRow = () => {
        table.setCreatingRow(null);
        table.setEditingRow(null);
        setactiveAction(-1);
        setValidationErrors({});
    };

    useImperativeHandle(ref, () => ({
        submitAndCheck: async () => {
            const { creatingRow, editingRow } = table.getState();

            if (!creatingRow && !editingRow) return true;

            const editedValues = creatingRow
                ? creatingRow._valuesCache
                : editingRow._valuesCache;

            const mode = creatingRow ? "add" : "edit";
            const validationErrors = validateFuel(editedValues, mode, {});
            if (Object.values(validationErrors).some(Boolean)) {
                setValidationErrors(validationErrors);
                return false;
            }
            setValidationErrors({});
            let isDirty = false;

            if (editingRow && editedValues && editingRow.original) {
                isDirty = Object.keys(editedValues).some((flatKey) => {
                    const newValue = editedValues[flatKey];

                    if (typeof newValue === "undefined") return false;

                    const originalValue = flatKey
                        .split(".")
                        .reduce((obj, key) => (obj ? obj[key] : undefined), editingRow.original);

                    return newValue !== originalValue;
                });
            }

            if (creatingRow) {
                await handleCreateFuel({ values: editedValues, table }, true);
                return true;
            }

            if (editingRow && isDirty) {
                await handleSaveFuel({ values: editedValues, table, row: editingRow }, true);
                return true;
            }

            if (editingRow && !isDirty) {
                resetActiveRow();
                table.setEditingRow(null);
                return true;
            }

            return true;
        }
    }));

    const validateRequired = (value) =>
        typeof value === "string" ? value?.length > 0 :
            typeof value === "number" ? value >= 0 :
                false;

    const validateFuel = (row, type, rowObject) => {
        const errors = {
            fuelOrigin: !validateRequired(row.fuelOrigin)
                ? "Field is Required"
                : "",
            fuelType: !validateRequired(row.fuelType)
                ? "Field is Required"
                : "",
            nameOfOtherFuel: "",
        };
        if (!row.fuelOrigin || !row.fuelType) return errors;

        if (row.fuelType?.toLowerCase().includes("other")) {
            if (!validateRequired(row.nameOfOtherFuel)) {
                errors.nameOfOtherFuel = "Field is Required";
            }
        };
        const isDuplicate = (fuelData || [])?.some((fuel) => {
            const isSameCombination =
                fuel.fuelOrigin === row.fuelOrigin &&
                fuel.FuelType === row.fuelType;
            if (type === "edit") {
                return (
                    isSameCombination &&
                    fuel.b4EmissionFactorId !== rowObject?.original?.b4EmissionFactorId
                );
            }
            return isSameCombination;
        });
        if (isDuplicate) {
            errors.fuelType = "Fuel Type is already in use";
        }
        return errors;
    };

    const alphanumericAndLengthValidation = (value, compareVal) => {
        return value.length <= compareVal
            ? value
            : value.slice(0, compareVal);
    };


    const buildFuelPayloadFromValues = (values) => {
        const { fuelType, fuelOrigin, nameOfOtherFuel, fuelDescription, bunkeredFuelDensity, b4EmissionFactorId, co2, ch4, n2o, } = values;
        const getMethodology = (gas, framework) => values?.[`${gas}.${framework}.methodologiesForEFs`] ?? "";
        const payload = {
            b4EmissionFactorId: b4EmissionFactorId ?? 0,
            fuelOrigin,
            fuelType,
            nameOfOtherFuel,
            bunkeredFuelDensity: (bunkeredFuelDensity || []),
            fuelDescription,
            co2: [{ ...co2.MRV, methodologiesForEFs: getMethodology("co2", "MRV") }, { ...co2.ETS, methodologiesForEFs: getMethodology("co2", "ETS") }],
            ch4: [{ ...ch4.MRV, methodologiesForEFs: getMethodology("ch4", "MRV") }, { ...ch4.ETS, methodologiesForEFs: getMethodology("ch4", "ETS") }],
            n2o: [{ ...n2o.MRV, methodologiesForEFs: getMethodology("n2o", "MRV") }, { ...n2o.ETS, methodologiesForEFs: getMethodology("n2o", "ETS") }],
        };
        return [payload];
    };

    const buildFuelPayloadFromValuesForSave = (values) => {
        const { fuelType, fuelOrigin, nameOfOtherFuel, fuelDescription, bunkeredFuelDensity, b4EmissionFactorId, co2, ch4, n2o, } = values;
        const payload = {
            b4EmissionFactorId: b4EmissionFactorId ?? 0,
            fuelOrigin,
            fuelType,
            nameOfOtherFuel,
            bunkeredFuelDensity: (bunkeredFuelDensity || []),
            fuelDescription,
            co2: [{ ...co2.MRV }, { ...co2.ETS }],
            ch4: [{ ...ch4.MRV }, { ...ch4.ETS }],
            n2o: [{ ...n2o.MRV }, { ...n2o.ETS }],
        };
        return [payload];
    };

    const handleCreateFuel = async ({ values, table, row }, isAuto = false) => {
        const newValidationErrors = validateFuel(values, "add", {});
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        setValidationErrors({});
        const payload = buildFuelPayloadFromValues(values);
        const newTransformedData = transformData(payload)?.map((entry, index) => ({
            ...entry,
            b4EmissionFactorId: 0,
        }));
        const updatedRowData = fuelData
            ?.map(item => buildFuelPayloadFromValues(item))
            ?.flat()
            ?.map(obj => ({
                ...obj,
                b4EmissionFactorId: 0,
            }));
        const finalPayload = [...updatedRowData, ...payload];
        try {
            const b4EmissionFactors = await callApi({
                method: "POST",
                url: API_ROUTES.B4(vesselMstId),
                data: finalPayload,
            });
            if (b4EmissionFactors?.isSuccess) {
                emitToasterMsg("success", "Table B.4", isAuto ? "Fuel type has been autosaved successfully" : "Fuel type has been added successfully");
                setFuelData((prev) => [...prev, ...newTransformedData]);
                table.setCreatingRow(null);
                table.setEditingRow(null);
                setactiveAction(-1);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table B.4", b4EmissionFactors?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table B.4", error?.errorMessage);
        }
    };

    const handleSaveFuel = async ({ values, table, row }, isAuto = false) => {
        const newValidationErrors = validateFuel(values, "edit", row);
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        const enrichedValues = {
            ...values,
            b4EmissionFactorId: row?.original?.b4EmissionFactorId ?? 0,
        };
        const payload = buildFuelPayloadFromValues(enrichedValues);
        const updatedItem = transformData(payload)[0];
        const existingIndex = fuelData?.findIndex(
            (item) => item.b4EmissionFactorId === row?.original?.b4EmissionFactorId
        );
        const updatedRowData = [...fuelData];
        if (existingIndex !== -1) {
            updatedRowData[existingIndex] = { ...updatedItem };
        } else {
            updatedRowData.push({ ...updatedItem });
        }
        const finalPayload = updatedRowData
            ?.map(item => buildFuelPayloadFromValuesForSave(item))
            ?.flat()
            ?.map(obj => ({
                ...obj,
                b4EmissionFactorId: 0,
            }));
        try {
            const b4EmissionFactors = await callApi({
                method: "POST",
                url: API_ROUTES.B4(vesselMstId),
                data: finalPayload,
            });
            if (b4EmissionFactors?.isSuccess) {
                emitToasterMsg("success", "Table B.4", isAuto ? "Fuel type has been autosaved successfully" : "Fuel type has been updated successfully");
                setFuelData(updatedRowData);
                table.setEditingRow(null);
                setactiveAction(-1);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            } else {
                emitToasterMsg("error", "Table B.4", b4EmissionFactors?.errorMessage || "Save failed");
            }
        } catch (error) {
            emitToasterMsg("error", "Table B.4", error?.errorMessage);
        }
    };

    const handleDeleteFuel = async (indexToDelete) => {
        const newData = fuelData?.filter((_, index) => index !== indexToDelete);
        const finalPayload = newData
            ?.map((item) => buildFuelPayloadFromValues(item))
            ?.flat();
        try {
            const b4EmissionFactors = await callApi({
                method: "POST",
                url: API_ROUTES.B4(vesselMstId),
                data: finalPayload,
            });
            if (b4EmissionFactors?.isSuccess) {
                emitToasterMsg("success", "Table B.4", "Fuel type has been Deleted successfully");
                setFuelData(newData);
                setactiveAction(-1);
                setConfirmDelete(false);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            } else {
                emitToasterMsg("error", "Table B.4", b4EmissionFactors?.errorMessage || "Delete failed");
                setConfirmDelete(false);
            }
        } catch (error) {
            emitToasterMsg("error", "Table B.4", error?.errorMessage);
        }
    };

    const handleDeleteBtnClick = (rowIndex) => {
        setConfirmDelete(true);
        setDeletingRowIndex(rowIndex);
    };

    const getFuelOriginOptions = () => {
        return fuelOriginMsts?.map(origin => ({
            label: origin.value,
            value: origin.value
        }));
    };

    const getFuelTypeOptions = (origin) => {
        const entry = fuelOriginMsts?.find((o) => o.value === origin);
        return entry?.fuelTypes?.map(type => ({
            label: type.value,
            value: type.fuelCode
        })) || [];
    };

    const getDensityMethodOptions = () => {
        return fuelBunkeredDensities?.map(origin => ({
            label: origin.value,
            value: origin.code
        }));
    }

    const densityMethodLabels = useMemo(getDensityMethodOptions, [fuelBunkeredDensities]);

    const densityMethodLabelByCode = useMemo(
        () => densityMethodLabels && Object?.fromEntries(densityMethodLabels?.map(o => [o.value, o.label])),
        [densityMethodLabels],
    );


    const getEmissionFactorsData = (selectedOrigin, selectedType) => {
        const originEntry = fuelOriginMsts?.find((origin) => origin.value === selectedOrigin);
        if (!originEntry) return [];
        const typeEntry = originEntry?.fuelTypes?.find((type) => type.fuelCode === selectedType);
        return typeEntry?.ttwEmissionFactors || [];
    }

    const handleFuelOriginChange = (e, row) => {
        const selectedFuelOrigin = e.target.value;
        const updatedRow = {
            ...row.original,
            fuelOrigin: selectedFuelOrigin,
            fuelType: "",
        };
        row._valuesCache.fuelOrigin = selectedFuelOrigin;
        row._valuesCache.fuelType = '';
        if (row.id === undefined || row.original?.isNewRow) {
            row.original.fuelType = '';
        }
        setEmissionFactorData([]);
        table.setEditingRow(updatedRow);
    };

    const handleFuelTypeChange = (e, row) => {
        const selectedFuelType = e.target.value;
        const fuelOrigin = row._valuesCache.fuelOrigin || row.original.fuelOrigin;
        const emissionFactors = getEmissionFactorsData(fuelOrigin, selectedFuelType);
        const includesOther = selectedFuelType?.toLowerCase().includes("other");
        if (!includesOther) {
            row._valuesCache.nameOfOtherFuel = "";
            row.original.nameOfOtherFuel = "";
        }
        const updatedRow = {
            ...row.original,
            fuelOrigin,
            fuelType: selectedFuelType,
            nameOfOtherFuel: includesOther ? row._valuesCache.nameOfOtherFuel : "",
        };
        gases.forEach((gas) => {
            updatedRow[gas] = updatedRow[gas] || {};
            frameworks.forEach((framework) => {
                const found = emissionFactors?.find(
                    (ef) => ef.ghg === gas?.toUpperCase() && ef.legalFrameWork === framework
                );
                updatedRow[gas][framework] = {
                    legalFrameWork: found.legalFrameWork ?? "",
                    ttwEf: found.ttwef ?? "",
                    slipEf: found.slipEF ?? "",
                    factorReference: "Default",
                    isTtwEditable: found.ttwefValueType === "Default",
                    isSlipEditable: found.slipEFValueType === "Default",
                };
            });
        });
        gases.forEach((gas) => {
            frameworks.forEach((framework) => {
                const value = updatedRow[gas]?.[framework];
                updatedRow[`${gas}.${framework}.ttwEf`] = value?.ttwEf ?? "";
                updatedRow[`${gas}.${framework}.slipEf`] = value?.slipEf ?? "";
                updatedRow[`${gas}.${framework}.factorReference`] = value?.factorReference ?? "";
                updatedRow[`${gas}.${framework}.legalFrameWork`] = value?.legalFrameWork ?? "";
                updatedRow[`${gas}.${framework}.isTtwEditable`] = value?.isTtwEditable ?? "";
                updatedRow[`${gas}.${framework}.isSlipEditable`] = value?.isSlipEditable ?? "";
                row._valuesCache[`${gas}.${framework}.methodologiesForEFs`] = "";
            });
        });
        row._valuesCache.fuelType = selectedFuelType;
        row._valuesCache.co2 = updatedRow.co2;
        row._valuesCache.ch4 = updatedRow.ch4;
        row._valuesCache.n2o = updatedRow.n2o;

        setEmissionFactorData(updatedRow)
    };

    const getTtwEfFieldProps = ({ gas, framework }) => {
        return ({ row }) => {
            const fuelOrigin = row._valuesCache.fuelOrigin || row.original.fuelOrigin;
            const fuelType = row._valuesCache.fuelType || row.original.fuelType;
            const nameOfOtherFuel = row._valuesCache.nameOfOtherFuel || row.original.nameOfOtherFuel;
            const bunkeredFuelDensity = row._valuesCache.bunkeredFuelDensity || row.original.bunkeredFuelDensity;
            const fuelDescription = row._valuesCache.fuelDescription || row.original.fuelDescription;
            const isEditable = emissionFactorData?.[gas]?.[framework]?.isTtwEditable;
            const currentValue = emissionFactorData?.[gas]?.[framework]?.ttwEf;
            return {
                type: "number",
                disabled: !isEditable,
                value: currentValue ?? "",
                onChange: (e) => {
                    const newValue = e.target.value;
                    const isOriginOrTypeChanged =
                        row.original.fuelOrigin !== fuelOrigin ||
                        row.original.fuelType !== fuelType;
                    let originalTtwEf = "";
                    if (isOriginOrTypeChanged) {
                        const emissionFactorsData = getEmissionFactorsData(fuelOrigin, fuelType);
                        const originalEntry = emissionFactorsData?.find(
                            (item) =>
                                item.fuelOrigin === fuelOrigin &&
                                item.fuelType === fuelType &&
                                item.ghg === gas.toUpperCase() &&
                                item.legalFrameWork === framework
                        );
                        originalTtwEf = originalEntry?.ttwef?.toString() ?? "";
                        row._valuesCache[gas] = {
                            ...row._valuesCache[gas],
                            [framework]: {
                                ...row._valuesCache[gas]?.[framework],
                                ttwEf: newValue,
                                factorReference:
                                    newValue !== "" && newValue !== originalTtwEf
                                        ? "Defined by user"
                                        : "Default",
                            },
                        };
                    }
                    else {
                        updateFieldsWhenFuelTypeUnchanged({
                            row,
                            gas,
                            framework,
                            newValue,
                            fuelOrigin,
                            fuelType,
                            nameOfOtherFuel,
                            bunkeredFuelDensity,
                            fuelDescription
                        });
                    }
                    setEmissionFactorData((prev) => ({
                        ...prev,
                        [gas]: {
                            ...prev[gas],
                            [framework]: {
                                ...prev[gas]?.[framework],
                                ttwEf: newValue,
                                factorReference:
                                    newValue !== "" &&
                                        newValue !== row.original?.[gas]?.[framework]?.ttwEf?.toString()
                                        ? "Defined by user"
                                        : row.original?.[gas]?.[framework]?.factorReference ?? "Default",
                            },
                        },
                    }));
                },
                onKeyDown: (e) => {
                    preventScientificNotation(e);
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                        e.preventDefault();
                    }
                },
                onWheel: (e) => {
                    e.target.blur();
                }
            };
        };
    };

    const updateFieldsWhenFuelTypeUnchanged = ({
        row,
        gas,
        framework,
        newValue,
        fuelOrigin,
        fuelType,
        nameOfOtherFuel,
        bunkeredFuelDensity,
        fuelDescription
    }) => {
        const updatedPayload = {
            fuelOrigin,
            fuelType,
            nameOfOtherFuel,
            bunkeredFuelDensity,
            fuelDescription,
            co2: { MRV: {}, ETS: {} },
            ch4: { MRV: {}, ETS: {} },
            n2o: { MRV: {}, ETS: {} }
        };

        gases.forEach((g) => {
            frameworks.forEach((f) => {
                const isThisGas = g === gas && f === framework;

                const cached = row._valuesCache?.[g]?.[f] || {};
                const original = row.original?.[g]?.[f] || {};

                const inputValue = isThisGas
                    ? newValue
                    : cached.ttwEf ?? original.ttwEf;

                const isTtwEfChanged =
                    isThisGas &&
                    newValue !== "" &&
                    newValue !== original?.ttwEf?.toString();

                updatedPayload[g][f] = {
                    legalFrameWork: f,
                    ttwEf: parseFloat(inputValue),
                    slipEf: cached.slipEf ?? original?.slipEf ?? 0,
                    factorReference: isTtwEfChanged
                        ? "Defined by user"
                        : cached.factorReference ?? original?.factorReference ?? "Default",
                    isTtwEditable: cached.isTtwEditable ?? original?.isTtwEditable ?? false,
                    isSlipEditable: cached.isSlipEditable ?? original?.isSlipEditable ?? false,
                    methodologiesForEFs: cached.methodologiesForEFs ?? original?.methodologiesForEFs ?? ""
                };
            });
        });

        row._valuesCache = {
            ...row._valuesCache,
            fuelOrigin,
            fuelType,
            fuelDescription,
            bunkeredFuelDensity,
            nameOfOtherFuel,
            co2: { ...row._valuesCache?.co2, ...updatedPayload.co2 },
            ch4: { ...row._valuesCache?.ch4, ...updatedPayload.ch4 },
            n2o: { ...row._valuesCache?.n2o, ...updatedPayload.n2o },
        };

        return updatedPayload;
    };

    const handleFieldChange = ({
        row,
        gas,
        framework,
        field,
        newValue,
        fuelOrigin,
        fuelType,
        nameOfOtherFuel,
        bunkeredFuelDensity,
        fuelDescription
    }) => {
        const isOriginOrTypeChanged =
            row.original.fuelOrigin !== fuelOrigin ||
            row.original.fuelType !== fuelType;

        // Always update field value (including ttwEf)
        if (isOriginOrTypeChanged) {
            if (field === "ttwEf") {
                const emissionFactorsData = getEmissionFactorsData(fuelOrigin, fuelType);
                const originalEntry = emissionFactorsData?.find(
                    (item) =>
                        item.fuelOrigin === fuelOrigin &&
                        item.fuelType === fuelType &&
                        item.ghg === gas.toUpperCase() &&
                        item.legalFrameWork === framework
                );
                const originalTtwEf = originalEntry?.ttwef?.toString() ?? "";

                row._valuesCache[gas] = {
                    ...row._valuesCache[gas],
                    [framework]: {
                        ...row._valuesCache[gas]?.[framework],
                        ttwEf: newValue,
                        factorReference:
                            newValue !== "" && newValue !== originalTtwEf
                                ? "Defined by user"
                                : "Default"
                    }
                };
            } else {
                row._valuesCache[field] = newValue;
            }
        } else {
            updateFieldsWhenFuelTypeUnchanged({
                row,
                gas,
                framework,
                field,
                newValue,
                fuelOrigin,
                fuelType,
                nameOfOtherFuel,
                bunkeredFuelDensity,
                fuelDescription
            });
        }
        // Optional: update factorReference in emissionFactorData only for ttwEf
        if (field === "ttwEf") {
            const original = row.original?.[gas]?.[framework];
            const factorRef =
                newValue !== "" && newValue !== original?.ttwEf?.toString()
                    ? "Defined by user"
                    : original?.factorReference ?? "Default";

            setEmissionFactorData((prev) => ({
                ...prev,
                [gas]: {
                    ...prev[gas],
                    [framework]: {
                        ...prev[gas]?.[framework],
                        ttwEf: newValue,
                        factorReference: factorRef
                    }
                }
            }));
        }
    };


    const createEmissionColumns = (gas) => ({
        id: `${gas}`,
        header: `Emission factor ${gas?.toUpperCase()} (g${gas?.toUpperCase()}/gFuel)`,
        muiTableHeadCellProps: {
            className: "grouped-header",
        },
        columns: frameworks?.map((framework) => ({
            id: `${gas}.${framework}`,
            header: `${framework}`,
            muiTableHeadCellProps: {
                className: "grouped-header",
            },
            columns: [
                {
                    accessorKey: `${gas}.${framework}.factorReference`,
                    header: "Factor reference",
                    muiEditTextFieldProps: () => ({
                        disabled: true,
                        value: emissionFactorData?.fuelOrigin && emissionFactorData?.fuelType
                            ? emissionFactorData?.[gas]?.[framework]?.factorReference ?? ""
                            : ""
                    })
                },
                {
                    accessorKey: `${gas}.${framework}.ttwEf`,
                    header: "TTW EF",
                    muiEditTextFieldProps: getTtwEfFieldProps({ gas: gas, framework: framework })
                },
                {
                    accessorKey: `${gas}.${framework}.slipEf`,
                    header: "Slip EF",
                    muiEditTextFieldProps: () => ({
                        disabled: true,
                        value: emissionFactorData?.fuelOrigin && emissionFactorData?.fuelType
                            ? emissionFactorData?.[gas]?.[framework]?.slipEf ?? ""
                            : ""
                    })
                },
                {
                    accessorKey: `${gas}.${framework}.methodologiesForEFs`,
                    header: "Methodologies for determining the EF's",
                    size: 230,
                    muiEditTextFieldProps: ({ row }) => ({
                        value: row?.getValue(`${gas}.${framework}.methodologiesForEFs`) || '',
                        disabled: !emissionFactorData?.[gas]?.[framework]?.isTtwEditable,
                        placeholder: "Methodologies",
                        onChange: (e) => {
                            const validatedValue = alphanumericAndLengthValidation(
                                e.target.value, 1000
                            );
                            row._valuesCache[`${gas}.${framework}.methodologiesForEFs`] = validatedValue;
                            handleFieldChange({
                                row,
                                gas: "",
                                framework: "",
                                field: `${gas}.${framework}.methodologiesForEFs`,
                                newValue: validatedValue,
                                fuelOrigin: row._valuesCache.fuelOrigin || row.original.fuelOrigin,
                                fuelType: row._valuesCache.fuelType || row.original.fuelType,
                                nameOfOtherFuel: row._valuesCache.nameOfOtherFuel || row.original.nameOfOtherFuel,
                                bunkeredFuelDensity: row._valuesCache.bunkeredFuelDensity || row.original.bunkeredFuelDensity,
                                fuelDescription: row._valuesCache.fuelDescription || row.original.fuelDescription
                            })


                        },
                    }),
                }
            ],
        })),
    });

    const columns = useMemo(
        () => [
            {
                accessorKey: "fuelOrigin",
                header: "Fuel origin",
                editVariant: "select",
                editSelectOptions: getFuelOriginOptions,
                muiEditTextFieldProps: ({ row, table }) => ({
                    required: true,
                    error: !!validationErrors?.fuelOrigin,
                    helperText: validationErrors?.fuelOrigin,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            fuelOrigin: undefined,
                        }),
                    onChange: (e) => handleFuelOriginChange(e, row),
                }),
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                    },
                },
            },
            {
                accessorKey: "fuelType",
                header: "Fuel type",
                editVariant: "select",
                size: 280,
                editSelectOptions: ({ row }) => {
                    const origin = row._valuesCache.fuelOrigin || row.original.fuelOrigin;
                    return getFuelTypeOptions(origin) || [];
                },
                Cell: ({ cell, row }) => {
                    const fuelOrigin = row.original.fuelOrigin || row._valuesCache.fuelOrigin;
                    const options = getFuelTypeOptions(fuelOrigin);
                    const value = cell.getValue() || "";
                    const matchedOption = options?.find((opt) => opt.value === value);
                    return matchedOption?.label || value || "";
                },
                muiEditTextFieldProps: ({ row }) => {
                    const fuelOrigin = row._valuesCache.fuelOrigin || row.original.fuelOrigin;
                    const fuelType = row._valuesCache.fuelType || row.original.fuelType || '';
                    return {
                        value: fuelType,
                        disabled: !fuelOrigin,
                        required: true,
                        error: !!validationErrors?.fuelType,
                        helperText: validationErrors?.fuelType,
                        onFocus: () =>
                            setValidationErrors({
                                ...validationErrors,
                                fuelType: undefined,
                            }),
                        onChange: (e) => handleFuelTypeChange(e, row),
                        SelectProps: {
                            MenuProps: {
                                disableScrollLock: true,
                                PaperProps: {
                                    sx: {
                                        maxHeight: 250,
                                        maxWidth: 250,
                                        boxShadow: 3,
                                        overflow: 'auto',
                                        whiteSpace: 'nowrap',
                                        '&::-webkit-scrollbar': {
                                            height: '8px',
                                            width: "8px"
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: 'darkgray',
                                            borderRadius: '3px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            backgroundColor: '#f0f0f0',
                                        },
                                    },
                                },
                                anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left",
                                },
                                transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                },
                                getContentAnchorEl: null,
                            }
                        }
                    };
                },
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                    },
                },
            },
            {
                accessorKey: "nameOfOtherFuel",
                header: "Name of the other fuel type",
                size: 230,
                muiEditTextFieldProps: ({ row }) => {
                    const fuelOrigin = row._valuesCache?.fuelOrigin || row.original?.fuelOrigin || "";
                    const fuelType = row._valuesCache.fuelType || row.original.fuelType;
                    console.log("mrvfuelorigin ", fuelOrigin)
                    const isOther =
                        fuelOrigin.toLowerCase().includes("biofuel") &&
                        fuelType.toLowerCase().includes("other");
                    return {
                        disabled: !isOther,
                        required: true,
                        error: !!validationErrors?.nameOfOtherFuel,
                        helperText: validationErrors?.nameOfOtherFuel,
                        onFocus: () =>
                            setValidationErrors({
                                ...validationErrors,
                                nameOfOtherFuel: undefined,
                            }),
                        value: isOther ? row._valuesCache.nameOfOtherFuel : "",
                        onChange: (e) => {
                            const validatedValue = alphanumericAndLengthValidation(
                                e.target.value, 255
                            )
                            row._valuesCache.nameOfOtherFuel = validatedValue;
                            handleFieldChange({
                                row,
                                gas: "",
                                framework: "",
                                field: "nameOfOtherFuel",
                                newValue: validatedValue,
                                fuelOrigin: row._valuesCache.fuelOrigin || row.original.fuelOrigin,
                                fuelType: row._valuesCache.fuelType || row.original.fuelType,
                                nameOfOtherFuel: validatedValue,
                                bunkeredFuelDensity: row._valuesCache.bunkeredFuelDensity || row.original.bunkeredFuelDensity,
                                fuelDescription: row._valuesCache.fuelDescription || row.original.fuelDescription
                            });
                        },
                    };
                },
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                    },
                },
            },
            {
                accessorKey: "bunkeredFuelDensity",
                header: "Method for determination of density of fuel bunkered",
                editVariant: 'select',
                size: 320,
                editSelectOptions: getDensityMethodOptions,
                Cell: ({ cell }) =>
                    (cell.getValue() || [])
                        .map(code => densityMethodLabelByCode?.[code] ?? code)
                        .join(', '),
                muiEditTextFieldProps: ({ cell, row }) => ({
                    select: true,
                    required: false,
                    value: cell.getValue() || [],
                    SelectProps: {
                        multiple: true,
                        renderValue: (selected) => {
                            if (!selected || selected.length === 0) return 'Select density method(s)';
                            return (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((code) => (
                                        <Chip
                                            key={code}
                                            label={densityMethodLabelByCode?.[code] ?? code}
                                            onDelete={(e) => {
                                                e.stopPropagation();
                                                const newValue = selected.filter((v) => v !== code);
                                                row._valuesCache["bunkeredFuelDensity"] = newValue;
                                                setValidationErrors?.((prev) => ({
                                                    ...prev,
                                                    bunkeredFuelDensity: undefined,
                                                }));
                                                handleFieldChange({
                                                    row,
                                                    gas: "",
                                                    framework: "",
                                                    field: "bunkeredFuelDensity",
                                                    newValue: newValue,
                                                    fuelOrigin: row._valuesCache.fuelOrigin || row.original.fuelOrigin,
                                                    fuelType: row._valuesCache.fuelType || row.original.fuelType,
                                                    nameOfOtherFuel: row._valuesCache.nameOfOtherFuel || row.original.nameOfOtherFuel,
                                                    bunkeredFuelDensity: newValue,
                                                    fuelDescription: row._valuesCache.fuelDescription || row.original.fuelDescription
                                                });
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        />
                                    ))}
                                </Box>
                            );
                        },
                        MenuProps: {
                            disableScrollLock: true,
                            PaperProps: {
                                sx: {
                                    maxHeight: 250,
                                    maxWidth: 280,
                                    overflowY: "auto",
                                    '&::-webkit-scrollbar': {
                                        width: "8px"
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'darkgray',
                                        borderRadius: '3px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: '#f0f0f0',
                                    },
                                },
                            },
                            anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                            },
                            transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                            },
                            getContentAnchorEl: null,
                        },
                    },
                    onChange: (event) => {
                        const value = event.target.value;
                        row._valuesCache["bunkeredFuelDensity"] = value;
                        handleFieldChange({
                            row,
                            gas: "",
                            framework: "",
                            field: "bunkeredFuelDensity",
                            newValue: value,
                            fuelOrigin: row._valuesCache.fuelOrigin || row.original.fuelOrigin,
                            fuelType: row._valuesCache.fuelType || row.original.fuelType,
                            nameOfOtherFuel: row._valuesCache.nameOfOtherFuel || row.original.nameOfOtherFuel,
                            bunkeredFuelDensity: value,
                            fuelDescription: row._valuesCache.fuelDescription || row.original.fuelDescription
                        });
                        setValidationErrors?.((prev) => ({
                            ...prev,
                            bunkeredFuelDensity: undefined,
                        }));
                    },
                }),
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle",
                    },
                },
            },
            {
                accessorKey: "fuelDescription",
                header: "Fuel description",
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                    },
                },
                muiEditTextFieldProps: ({ row }) => ({
                    required: false,
                    onChange: (e) => {
                        const validatedValue = alphanumericAndLengthValidation(
                            e.target.value, 1000
                        )
                        e.target.value = validatedValue;
                        handleFieldChange({
                            row,
                            gas: "",
                            framework: "",
                            field: "fuelDescription",
                            newValue: validatedValue,
                            fuelOrigin: row._valuesCache.fuelOrigin || row.original.fuelOrigin,
                            fuelType: row._valuesCache.fuelType || row.original.fuelType,
                            nameOfOtherFuel: row._valuesCache.nameOfOtherFuel || row.original.nameOfOtherFuel,
                            bunkeredFuelDensity: row._valuesCache.bunkeredFuelDensity || row.original.bunkeredFuelDensity,
                            fuelDescription: validatedValue
                        });
                    },
                }),
            },
            ...gases?.map((gas) => createEmissionColumns(gas))

        ],
        [validationErrors, fuelData, emissionFactorData]
    );

    const table = useMaterialReactTable({
        columns,
        data: fuelData || [],
        enableStickyHeader: true,
        enableSorting: false,
        enableRowSelection: false,
        enableColumnPinning: false,
        enableTopToolbar: true,
        enableBottomToolbar: false,
        enableColumnActions: false,
        createDisplayMode: "row",
        editDisplayMode: "row",
        enableEditing: true,
        enablePagination: false,
        muiTableHeadCellProps: {
            sx: {
                color: "rgb(255 255 255)",
                backgroundColor: "#042a4c",
                position: "sticky",
                borderRight: "1px solid lightgray",
                "&:last-child": {
                    borderRight: "none",
                },
            },
        },
        muiTablePaperProps: {
            sx: {
                boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: "350px",
                overflowY: "auto",
            },
        },
        displayColumnDefOptions: {
            'mrt-row-actions': {
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                    },
                },
            },
        },
        muiTableBodyCellProps: () => {
            return {
                sx: {
                    borderRight: "1px solid lightgray",
                    "&:last-child": {
                        borderRight: "none",
                    },
                    color: "",
                },
            };
        },
        muiTableBodyRowProps: () => ({
            sx: {
                "&:not(:last-of-type)": {
                    borderBottom: "1px solid lightgray",
                },
                height: "60px",
                verticalAlign: "middle",
            },
        }),
        muiTableHeadRowProps: {
            sx: {
                borderBottom: "2px solid lightgray",
            },
        },
        getRowId: (row) => {
            return row?.original?.b4EmissionFactorId
        },
        onCreatingRowSave: handleCreateFuel,
        onEditingRowCancel: resetActiveRow,
        onCreatingRowCancel: resetActiveRow,
        onEditingRowSave: handleSaveFuel,
        renderToolbarInternalActions: () => <></>,
        renderRowActions: ({ row, table }) => {
            return (
                <Box sx={{ display: "flex", gap: "2rem" }}>
                    <Tooltip title="Edit">
                        <IconButton
                            style={{ padding: "0px" }}
                            onClick={() =>
                                handleFuelActions(
                                    table,
                                    row,
                                    RowActions.Edit
                                )}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            style={{ padding: "0px" }}
                            color="error"
                            onClick={() => handleDeleteBtnClick(row?.index, true)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        },
        renderTopToolbarCustomActions: ({ table }) => (
            <div className="mt-1">
                <Button
                    startIcon={<Icon icon={ICONS.ADD} />}
                    onClick={() => {
                        handleFuelActions(table, null, RowActions.Add);
                    }}
                    disabled={!!table.getState().creatingRow || !!table.getState().editingRow}
                >
                    Add fuel type
                </Button>
            </div>
        ),
    });

    return (
        <>
            <div className="b4-emission-factors mt-1">
                <MaterialReactTable table={table} />
            </div>
            <div className="confirmation-modal-container">
                <Modal
                    isOpen={confirmDelete}
                    modalHeader
                    padding={"70px"}
                    headerHeight={"50px"}
                    showFooter
                    onCloseClickListener={() => {
                        setConfirmDelete(false);
                    }}
                    footer={
                        <div className="d-flex justify-content-end fixed-action-btns p-4">
                            <Button
                                className="modal-delete-btn"
                                onClick={() => { handleDeleteFuel(deletingRowIndex) }}
                                primaryColor={COLORS.RED.DARK}
                                primaryBackgroundColor={COLORS.RED.LIGHT}
                            >
                                Delete
                            </Button>
                            <Button
                                className="modal-cancel-btn"
                                onClick={() => {
                                    setConfirmDelete(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    }
                >
                    <div>Are you sure you want to delete this fuel type?</div>
                </Modal>
            </div>
        </>
    );
});

export default B4EmissionFactors;